import { fetchAndActivate, getString, getValue } from 'firebase/remote-config';
import React, {
  createContext, FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { QueryLazyOptions, useLazyQuery } from '@apollo/client';
import { PROFILE, ProfileOutput } from './api/profile';
import { remoteConfig } from './client/firebase';

export type RevalidateData = {
  type: 'upap' | 'normal',
  id: string,
  subtitle: string,
}

interface GlobalContextInterface {
  executeAfterFetchProfile: (fn: () => void | Promise<void>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getGlobalProfile: (options?: QueryLazyOptions<Record<string, any>> | undefined) => void;
  profile: { data?: ProfileOutput; loading: boolean, called: boolean };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchProfile: (force?: any) => void;
  block?: boolean;
  handleBlock: (value: boolean) => void;
  isSprintCourse?: boolean;
  isPracticalCourses?: boolean;
  isSinaremCourse?: boolean;
  revalidateData: RevalidateData[];
}

const defaultData: GlobalContextInterface = {
  executeAfterFetchProfile: () => undefined,
  getGlobalProfile: () => undefined,
  profile: { data: undefined, loading: false, called: false },
  refetchProfile: () => undefined,
  handleBlock: () => undefined,
  isSprintCourse: false,
  revalidateData: [],
};

export const GlobalContext = createContext<GlobalContextInterface>(defaultData);

export const GlobalStorage: FC = ({ children }) => {
  const queue = useRef<(() => void | Promise<void>)[]>([]);
  const [block, setBlock] = useState(false);
  const [sprintOnlyId, setSprintOnlyId] = useState<string>('');
  const [practicalCoursesId, setPracticalCoursesId] = useState<string>('');
  const [sinaremCourseId, setSinaremCourseId] = useState<string>('');
  const [revalidateData, setRevalidateData] = useState<RevalidateData[]>([]);

  const executeAfterFetchProfile = useCallback((fn: () => void | Promise<void>) => {
    queue.current.push(fn);
  }, []);

  const [getGlobalProfile, {
    error: profileError, data, loading, called,
  }] = useLazyQuery<ProfileOutput>(PROFILE, {
    fetchPolicy: 'network-only',
    onCompleted: d => {
      if (d) {
        while (queue.current.length) {
          const fn = queue.current.shift();
          if (fn) {
            fn();
          }
        }
      }
    },
  });

  const course = useMemo(() => {
    if (!data || !data.profile.courses.length) {
      return undefined;
    }

    return data.profile.courses[0];
  }, [data]);

  const isSprintCourse = useMemo(() => {
    if (!course) {
      return undefined;
    }

    if (sprintOnlyId === course.course) {
      return true;
    }

    return false;
  }, [course, sprintOnlyId]);

  const isPracticalCourses = useMemo(() => {
    if (!course) {
      return undefined;
    }

    if (practicalCoursesId === course.course) {
      return true;
    }

    return false;
  }, [course, practicalCoursesId]);

  const isSinaremCourse = useMemo(() => {
    if (!course) {
      return undefined;
    }

    if (sinaremCourseId === course.course) {
      return true;
    }

    return false;
  }, [course, sinaremCourseId]);

  const loadRemoteConfigData = useCallback(async () => {
    try {
      await fetchAndActivate(remoteConfig);
      const sprintId = getString(remoteConfig, 'sprintCourseId');
      const practicalId = getString(remoteConfig, 'practicalCoursesId');
      const sinaremId = getString(remoteConfig, 'sinaremCourseId');
      setSprintOnlyId(sprintId);
      setPracticalCoursesId(practicalId);
      setSinaremCourseId(sinaremId);
      const rev: RevalidateData[] = JSON.parse(getValue(remoteConfig, 'revalidateData').asString() || '[]');
      setRevalidateData(rev);
    } catch (error) {
      console.error('Error fetching remote config:', error);
    }
  }, []);

  useEffect(() => {
    loadRemoteConfigData();
  }, [loadRemoteConfigData]);

  const refetchProfile = useCallback((force = false) => {
    if ((!called && !loading) || force) {
      getGlobalProfile();
    }
  }, [called, getGlobalProfile, loading]);

  useEffect(() => {
    if (data?.profile._id && data?.profile.courses.length && data?.profile.courses[0].course) {
      try {
        window.StonlyWidget(
          'identify',
          data?.profile._id,
          {
            userid: data?.profile._id,
            createdat: new Date(),
            courseid: data?.profile.courses[0].course,
          },
        );
      } catch (e) {
        console.log(e);
      }
    }
  }, [data?.profile]);

  useEffect(() => {
    if ((profileError)) {
      getGlobalProfile();
    }
  }, [profileError, getGlobalProfile]);

  return (
    <GlobalContext.Provider
      value={{
        executeAfterFetchProfile,
        getGlobalProfile,
        profile: { data, loading, called },
        refetchProfile,
        block,
        handleBlock: (value: boolean) => {
          setBlock(value);
        },
        isSprintCourse,
        isPracticalCourses,
        isSinaremCourse,
        revalidateData,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
